<script setup lang="ts">
import { ref, computed, type UnwrapRef, watch, toValue } from 'vue'
import InputText from '@/components/input-text.vue'
import InputLabel from '@/components/input-label.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import InputCheckbox from '@/components/input-checkbox.vue'
import ModalContent from '@/components/modal/modal-content.vue'
import { useQuery } from '@tanstack/vue-query'
import { checkAssetCreationEligibilityQueryOptions } from '@/features/assets/queries'
import { AssetType } from '@/features/assets/constants'
import { queryKeys } from '@/features/assets/queryKeys'
import { ValidationError } from '@/shared/api/validation'
import { useForm } from 'vee-validate'
import { faMagnifyingGlass, faTimes } from '@fortawesome/pro-regular-svg-icons'
import ButtonPrimary from '@/components/button-primary.vue'
import ModalFooter from '@/components/modal/modal-footer.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

defineEmits<{ close: [] }>()

const { values, setFieldError, handleSubmit } = useForm({
  validationSchema: toTypedSchema(
    z.discriminatedUnion('isWheelingProject', [
      z.object({
        isWheelingProject: z.literal(false),
        unifiiProjectCode: z.coerce.number().int().gt(0),
      }),
      z.object({
        isWheelingProject: z.literal(true),
        unifiiProjectCode: z.coerce.number().int().gt(0),
        wheelingProjectCode: z.string().min(1),
      })
    ])
  ),
})

const cachedQueryKey = ref<Parameters<typeof queryKeys.assets.creationEligibilityLookup>[0]>({
  assetType: AssetType.Solar,
  unifiiProjectCode: undefined,
  wheelingProjectCode: undefined,
})

const performLookup = handleSubmit(
  () => {
    // This is to force a change in query key and to force the refetch.
    cachedQueryKey.value = {
      assetType: values.isWheelingProject ? AssetType.Wheeling : AssetType.Solar,
      unifiiProjectCode: values.unifiiProjectCode,
      wheelingProjectCode: values.isWheelingProject ? values.wheelingProjectCode : undefined,
    }

    refetch()
  }
)
const details = defineModel<UnwrapRef<typeof data>>('details')
const { data, error, isFetching, refetch } = useQuery({
  ...checkAssetCreationEligibilityQueryOptions({
    assetType: () => toValue(cachedQueryKey.value.assetType),
    unifiiProjectCode: () => toValue(cachedQueryKey.value.unifiiProjectCode),
    wheelingProjectCode: () => toValue(cachedQueryKey.value.wheelingProjectCode),
  }),
})

watch(
  error,
  err => {
    if (err instanceof ValidationError) {
      // If there's a wheeling error, update the specific field.
      const wheelingProjectCodeError = err.errors.find(x => x.code?.toLowerCase() === 'wheeling_project_not_found')
      if (wheelingProjectCodeError) {
        setFieldError('wheelingProjectCode', wheelingProjectCodeError.message ?? 'Unable to verify whether this wheeling project is valid.')
        return
      }

      const projectCodeError = err.errors.find(x => x.code?.toLowerCase() === 'project_not_found')
      const assetAlreadyExistsError = err.errors.find(x => x.code?.toLowerCase() === 'asset_already_exists')
      const errorMessage = projectCodeError?.message ?? assetAlreadyExistsError?.message

      if (errorMessage) {
        setFieldError('unifiiProjectCode', errorMessage)
      } else {
        setFieldError('unifiiProjectCode', 'Unable to verify whether this project code is valid.')
      }
    } else if (err) {
      setFieldError('unifiiProjectCode', 'Unable to verify whether this project code is valid.')
    } else {
      setFieldError('unifiiProjectCode', undefined)
    }
  }
)

watch(
  data,
  value => {
    if (value) {
      details.value = value
    }
  }
)
</script>

<template>
  <ModalTitle>New Asset</ModalTitle>
  <ModalContent>
    <div class="d-grid align-items-center" style="grid-template-columns: max-content 50%; gap: 13px">
      <InputLabel>Unifii Project Code</InputLabel>
      <InputText name="unifiiProjectCode" error-variant="tooltip" @change="details = undefined" />

      <InputLabel>Is this an EnergyPro project?</InputLabel>
      <InputCheckbox name="isWheelingProject" />

      <template v-if="values.isWheelingProject">
        <InputLabel>EnergyPro project code</InputLabel>
        <InputText name="wheelingProjectCode" error-variant="tooltip" />
      </template>
    </div>
  </ModalContent>
  <ModalFooter class="text-end">
    <ButtonSecondary :disabled="isFetching" :icon="faTimes" @click="$emit('close')">Cancel</ButtonSecondary>
    <ButtonPrimary class="ms-2" :state="isFetching ? 'pending' : undefined" :icon="faMagnifyingGlass" @click="performLookup">Search</ButtonPrimary>
  </ModalFooter>
</template>

<style scoped lang="scss">

</style>
